import React, { useEffect, useRef, useState } from "react";
import { Badge, Button, Row, Col } from "react-bootstrap";
import receiptDefaultImage from "../../assets/empty-boost-proof-photo.jpg";

/**
 * Односкриншотный загрузчик для ассигментов с одним proof screenshot.
 * props:
 *  - onFilesUpload?: (result) => void    // вернёт { [fieldName]: { fileName, hash } }
 *  - fieldName?: string                  // ключ поля в объекте результата, по умолчанию 'proof'
 *  - waitingText?: string                // тексты бейджей/кнопки под конкретный тип
 *  - okText?: string
 *  - errText?: string
 *  - uploadBtnText?: string              // 'Upload Proof Screenshot'
 *  - reuploadBtnText?: string            // 'Re-Upload Proof Screenshot'
 *  - noteText?: string                   // нижний инфо-текст
 */
const ProofUploadSingle = ({
                               onFilesUpload,
                               fieldName = "proof",
                               waitingText = "Waiting for action proof screenshot...",
                               okText = "Proof: Uniqueness OK",
                               errText = "Proof: Duplicate Detected",
                               uploadBtnText = "Upload Proof Screenshot",
                               reuploadBtnText = "Re-Upload Proof Screenshot",
                               titleText = "Upload a screenshot that proves your completed action:",
                               noteText = "Upload one clear screenshot that proves the assigned action was completed. Avoid sensitive data; only the relevant confirmation, book/review context, and completed action need to be visible."
                           }) => {
    const [preview, setPreview] = useState(receiptDefaultImage);
    const [file, setFile] = useState(null);
    const [status, setStatus] = useState(""); // "", "OK", "NO"

    const [blinkOpacity, setBlinkOpacity] = useState(1);
    const blinkIntervalRef = useRef(null);

    const inputRef = useRef(null);

    // мерцание бейджа при ожидании/ошибке
    useEffect(() => {
        const needBlink = !status || status === "NO";
        if (needBlink) {
            blinkIntervalRef.current = setInterval(() => {
                setBlinkOpacity((p) => (p === 1 ? 0.5 : 1));
            }, 500);
        } else {
            clearInterval(blinkIntervalRef.current);
            setBlinkOpacity(1);
        }
        return () => clearInterval(blinkIntervalRef.current);
    }, [status]);

    const uploadToTemp = async (files) => {
        const formData = new FormData();
        files.forEach((f) => formData.append("photos", f));

        const res = await fetch(`${process.env.REACT_APP_API_URL}api/assignment/proof_temp`, {
            method: "POST",
            body: formData,
        });

        const data = await res.json();
        if (!res.ok) throw new Error(data?.message || "Upload failed");

        return {
            uploadedFiles: data.uploadedFiles || [],
            fileHashes: data.fileHashes || [],
            duplicates: data.duplicates || null,
        };
    };

    const handlePick = () => inputRef.current?.click();

    const handleChange = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setFile(f);
        setPreview(URL.createObjectURL(f));

        try {
            const { uploadedFiles, fileHashes } = await uploadToTemp([f]);
            setStatus("OK");

            const result = {
                [fieldName]: {
                    fileName: uploadedFiles[0],
                    hash: fileHashes[0],
                },
            };
            onFilesUpload?.(result);
        } catch {
            setStatus("NO");
        }
    };

    const BadgeByStatus = ({ status, waitingText, okText, errText }) => {
        if (!status) {
            return (
                <Badge
                    bg="secondary"
                    className="px-2 py-1"
                    style={{ fontSize: "0.875rem", fontWeight: "bold", opacity: blinkOpacity, transition: "opacity 0.5s" }}
                >
                    <i className="fa-solid fa-hourglass-half me-2 fa-spin" />
                    {waitingText}
                </Badge>
            );
        }
        if (status === "OK") {
            return (
                <Badge bg="success" className="px-2 py-1" style={{ fontSize: "0.875rem", fontWeight: "bold" }}>
                    <i className="fa-solid me-2 fa-check" />
                    {okText}
                </Badge>
            );
        }
        return (
            <Badge
                bg="danger"
                className="px-2 py-1"
                style={{ fontSize: "0.875rem", fontWeight: "bold", opacity: blinkOpacity, transition: "opacity 0.5s" }}
            >
                <i className="fa-solid me-2 fa-ban" />
                {errText}
            </Badge>
        );
    };

    return (
        <div className="w-100">
            <Row className="g-3">
                <Col xs={6}>
                    <div
                        className="d-flex flex-column align-items-center position-relative w-100 p-2"
                        style={{ minHeight: "auto", gap: "0.75rem" }}
                    >
                        <div className="position-absolute" style={{ top: 8, right: 8, zIndex: 1 }}>
                            <BadgeByStatus status={status} waitingText={waitingText} okText={okText} errText={errText} />
                        </div>

                        <img
                            src={preview}
                            alt="Proof screenshot preview"
                            className="img-fluid border border-secondary rounded"
                            style={{
                                maxHeight: "320px",
                                width: "100%",
                                objectFit: "contain",
                                borderRadius: 4,
                            }}
                        />

                        <input
                            type="file"
                            accept="image/*"
                            ref={inputRef}
                            className="d-none"
                            onChange={handleChange}
                        />

                        <Button variant="success" className="mt-1 btn-sm" onClick={handlePick}>
                            {status === "OK" ? reuploadBtnText : uploadBtnText}
                        </Button>
                    </div>
                </Col>

                {/* Info text */}
                <Col className="mt-2 text-muted">
                    <h6 className="mb-2 mt-2 mt-0 text-muted">
                        {titleText}
                    </h6>
                    {noteText}
                </Col>
            </Row>
        </div>
    );
};

export default ProofUploadSingle;
